import type { TFunction } from "i18next";
import type { Route } from "../../../../../../store/admin/routesStore";
import type { ThemeClasses } from "../types";
import { Badge } from "../../../../../../components/ui";

interface RouteSummaryProps {
  route: Route;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  t: TFunction;
}

const formatDistance = (km?: number) => {
  if (!km && km !== 0) return "—";
  return `${km.toFixed(1)} km`;
};

const formatDuration = (minutes?: number) => {
  if (!minutes && minutes !== 0) return "—";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
};

export function RouteSummary({ route, themeClasses, t }: RouteSummaryProps) {
  const bg = "bg-app";
  const waypointCount = route.principal_route?.length ?? 0;

  return (
    <div className={`mt-2 p-3 rounded-lg border ${themeClasses.cardBorder} ${bg}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${themeClasses.text} truncate`}>
            {route.name || t("trips.untitled")}
          </p>
          {route.description && (
            <p className={`text-xs ${themeClasses.textSecondary} mt-0.5 line-clamp-2`}>
              {route.description}
            </p>
          )}
        </div>
        {route.status?.name && (
          <Badge variant="outline" size="sm">
            {route.status.name}
          </Badge>
        )}
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className={`p-2 rounded-md bg-subtle`}>
          <div className="flex items-center gap-1 mb-0.5">
            <i className={`ri-road-map-line text-xs ${themeClasses.textSecondary}`}></i>
            <span className={`text-[10px] ${themeClasses.textSecondary}`}>Distancia</span>
          </div>
          <p className={`text-xs font-semibold ${themeClasses.text}`}>
            {formatDistance(route.principal_route_distance)}
          </p>
        </div>
        <div className={`p-2 rounded-md bg-subtle`}>
          <div className="flex items-center gap-1 mb-0.5">
            <i className={`ri-time-line text-xs ${themeClasses.textSecondary}`}></i>
            <span className={`text-[10px] ${themeClasses.textSecondary}`}>Duración</span>
          </div>
          <p className={`text-xs font-semibold ${themeClasses.text}`}>
            {formatDuration(route.principal_route_duration)}
          </p>
        </div>
        <div className={`p-2 rounded-md bg-subtle`}>
          <div className="flex items-center gap-1 mb-0.5">
            <i className={`ri-map-pin-line text-xs ${themeClasses.textSecondary}`}></i>
            <span className={`text-[10px] ${themeClasses.textSecondary}`}>Puntos</span>
          </div>
          <p className={`text-xs font-semibold ${themeClasses.text}`}>{waypointCount}</p>
        </div>
      </div>

      {/* Origin / Destination radii */}
      {(route.first_point_radius || route.last_point_radius) && (
        <div className="flex gap-3 text-xs mb-2">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-success shrink-0"></span>
            <span className={themeClasses.textSecondary}>Origen:</span>
            <span className={`font-medium ${themeClasses.text}`}>
              {route.first_point_radius ?? 500} m
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-error shrink-0"></span>
            <span className={themeClasses.textSecondary}>Destino:</span>
            <span className={`font-medium ${themeClasses.text}`}>
              {route.last_point_radius ?? 500} m
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
